// ============================================================================
// biomes.js — the palette of lands. The tactical field geometry is fixed, but a
// biome re-skins every environment channel (ground texture + tint, sky, fog,
// the three lights, the drifting ambient cloud, and the fortress stone) so the
// same battlefield reads as ash-flats, burnt forest, snow tundra, sea-coast,
// golden farmland, grey highlands or a sickly marsh. Textures degrade gracefully
// to the ashlands art if a biome file is missing.
//
// applyBiome() is the single entry point: it drives renderer + fx + battlefield
// + crowd together so a Conquest battle can set its whole look in one call.
// ============================================================================

// env fields consumed by renderer.applyEnv(); struct/particle consumed here.
// fogFar is pulled back + lights lifted vs. the pure-mood values so the arena's
// landmarks and silhouettes actually READ across the field (they were being
// swallowed by gloom before). Still grimdark, just legible.
export const BIOMES = {
  ashlands: {
    name: 'the Ash-Flats',
    ground: 'art/ground.png', groundTint: 0xdedede, groundEmissive: 0xff5210, groundEmisInt: 0.42,
    sky: 'art/sky.png', fog: 0x2a2320, fogNear: 46, fogFar: 300,
    sun: 0xffa05a, sunInt: 1.3, hemiSky: 0x3a4658, hemiGround: 0x2a1c14, hemiInt: 1.08, amb: 0x4a321e, ambInt: 0.66,
    particle: 'ash', struct: 0xcfc7bd, structEmis: 0.22,
  },
  emberwood: {
    name: 'the Emberwood',
    ground: 'art/ground_emberwood.png', groundTint: 0xbcc2aa, groundEmissive: 0xff6a1a, groundEmisInt: 0.16,
    sky: 'art/sky_emberwood.png', fog: 0x22271f, fogNear: 40, fogFar: 262,
    sun: 0xffd08a, sunInt: 1.16, hemiSky: 0x44506a, hemiGround: 0x25301e, hemiInt: 1.14, amb: 0x36402a, ambInt: 0.66,
    particle: 'leaves', struct: 0x9aa08c, structEmis: 0.12,
  },
  frostmark: {
    name: 'the Frostmark',
    ground: 'art/ground_frostmark.png', groundTint: 0xe6edf5, groundEmissive: 0x2a3a55, groundEmisInt: 0.05,
    sky: 'art/sky_frostmark.png', fog: 0xb9c4d4, fogNear: 54, fogFar: 330,
    sun: 0xdfeaff, sunInt: 1.24, hemiSky: 0x9fb4d0, hemiGround: 0x6a7488, hemiInt: 1.3, amb: 0x9ab0c8, ambInt: 0.72,
    particle: 'snow', struct: 0xcdd6e2, structEmis: 0.0,
  },
  saltmere: {
    name: 'the Saltmere Coast',
    ground: 'art/ground_saltmere.png', groundTint: 0xb8bcc0, groundEmissive: 0x1e3038, groundEmisInt: 0.05,
    sky: 'art/sky_saltmere.png', fog: 0x6c757c, fogNear: 46, fogFar: 300,
    sun: 0xc8d2d8, sunInt: 1.16, hemiSky: 0x6a7c88, hemiGround: 0x3e4a50, hemiInt: 1.2, amb: 0x60707c, ambInt: 0.66,
    particle: 'none', struct: 0xa8adb2, structEmis: 0.03,
  },
  goldfen: {
    name: 'the Goldfen Vale',
    ground: 'art/ground_goldfen.png', groundTint: 0xe0cd9c, groundEmissive: 0x6a4a1e, groundEmisInt: 0.06,
    sky: 'art/sky_goldfen.png', fog: 0x9a8a62, fogNear: 50, fogFar: 320,
    sun: 0xffe6a8, sunInt: 1.28, hemiSky: 0x9a9060, hemiGround: 0x6a5a36, hemiInt: 1.2, amb: 0x7a6a48, ambInt: 0.72,
    particle: 'pollen', struct: 0xc2b48c, structEmis: 0.05,
  },
  ironpeak: {
    name: 'the Ironpeaks',
    ground: 'art/ground_ironpeak.png', groundTint: 0xc4c7cc, groundEmissive: 0x24282e, groundEmisInt: 0.04,
    sky: 'art/sky_ironpeak.png', fog: 0x565b62, fogNear: 48, fogFar: 300,
    sun: 0xc4ccd4, sunInt: 1.14, hemiSky: 0x5c6672, hemiGround: 0x3a4048, hemiInt: 1.16, amb: 0x565e68, ambInt: 0.66,
    particle: 'none', struct: 0x9498a0, structEmis: 0.02,
  },
  duskmarsh: {
    name: 'the Duskmarsh',
    ground: 'art/ground_duskmarsh.png', groundTint: 0xa4b280, groundEmissive: 0x2a3a1e, groundEmisInt: 0.08,
    sky: 'art/sky_duskmarsh.png', fog: 0x3c4632, fogNear: 36, fogFar: 250,
    sun: 0xaac07a, sunInt: 1.04, hemiSky: 0x50603c, hemiGround: 0x2c341f, hemiInt: 1.14, amb: 0x46542f, ambInt: 0.64,
    particle: 'pollen', struct: 0x808a6a, structEmis: 0.04,
  },
};

export function biomeName(key) { return (BIOMES[key] || BIOMES.ashlands).name; }

// theme = { renderer, fx, crowd, battlefield }; enemy = { color, ember } | null
export function applyBiome(key, theme, enemy) {
  const b = BIOMES[key] || BIOMES.ashlands;
  if (theme.renderer) theme.renderer.applyEnv(b);
  if (theme.fx) theme.fx.setAmbient(b.particle);
  if (theme.battlefield) theme.battlefield.setStructTint(b.struct, b.structEmis);
  if (theme.crowd) {
    if (enemy) theme.crowd.setEnemyTint(enemy.color, enemy.ember);
    else theme.crowd.setEnemyTint(0xcf6038, 0xff5a12); // default molten Magmar
  }
}
